import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
} from 'react-router';
import type { LinksFunction } from 'react-router';
import { SiteHeader } from '~/components/layout/site-header';
import { SiteFooter } from '~/components/layout/site-footer';
import { ActionBar } from '~/components/layout/action-bar';
import { SITE } from '~/data/site';
import './app.css';

export const links: LinksFunction = () => [
  // Icons. Generated by scripts/generate-favicons.mjs as part of `npm run build`,
  // so they can never drift from the brand tokens.
  //
  // The rasters and the .ico are opaque Night to their own edges, which stops an
  // iOS home screen or a Windows shortcut painting a white ring behind a
  // transparent icon. favicon.svg is the opposite on purpose: transparent and
  // theme-aware, because a dark opaque icon on a dark TAB STRIP makes Chrome and
  // Safari draw their own light plate behind it. See the header of the generator.
  { rel: 'icon', href: '/favicon.ico', sizes: '48x48' },
  { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml', sizes: 'any' },
  { rel: 'apple-touch-icon', href: '/apple-touch-icon.png', sizes: '180x180' },
  { rel: 'manifest', href: '/site.webmanifest' },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Anton&family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap',
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#0e5b54" />
        <Meta />
        <Links />
      </head>
      <body className="has-action-bar">
        <a className="skip-link" href="#main">
          Skip to content
        </a>
        <SiteHeader />
        <main id="main">{children}</main>
        <SiteFooter />
        <ActionBar />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary() {
  const error = useRouteError();
  const is404 = isRouteErrorResponse(error) && error.status === 404;

  return (
    <div className="section">
      <div className="shell-narrow stack">
        <p className="eyebrow">{is404 ? 'Page not found' : 'Something went wrong'}</p>
        <h1>{is404 ? "That page isn't here." : 'This page hit an error.'}</h1>
        <p className="lede">
          {is404
            ? 'It may have moved. The quickest route to an answer is an email.'
            : 'Sorry about that. Email us and we will sort it out directly.'}
        </p>
        <div className="btn-row">
          <a className="btn btn-primary" href="/">
            Back to the homepage
          </a>
          <a className="btn btn-secondary" href={`mailto:${SITE.contact.email}`}>
            Email {SITE.contact.email}
          </a>
        </div>
      </div>
    </div>
  );
}
